// Simplified AuthService for frontend2
// Based on frontend/src/services/auth/auth.service.tsx

export interface PlbAuthState {
  sub: string;
  name: string;
  email: string;
  tenant_id: string;
  roles: Array<string>;
  tenant_name: string;
  access_token: string;
  profile_img_url?: string;
  tenants?: Array<{
    tenantId: string;
    name: string;
  }>;
  customClaims?: Record<string, any>;
}

declare global {
  interface Window {
    analytics: any;
    thena: any;
  }
}

export default class AuthService {
  private static instance: AuthService;
  
  authUrl: string;
  authAppUrl: string;
  useExternalLogin: boolean;
  feClientId: string | undefined;
  feAppId: string | undefined;
  plbAuthState: PlbAuthState | null = null;
  isCustomDomain: boolean = false;
  ssoLoginUrl: string | null = null;
  returnToken: string | null = null;
  legacyAppUrl: string | undefined;

  constructor() {
    const host = window.location.host;
    this.useExternalLogin = false;
    if (host.startsWith('local.') || host.startsWith('localhost')) {
      // frontend2 now runs on :3000, legacy on :3001
      this.authUrl = "https://auth-dev.whetstonedata.com";
      this.authAppUrl = "https://local.whetstonedata.com:3000";
      this.feClientId = '692d0655-fa36-4d1a-8dde-8d45c64bf1ef';
      this.feAppId = '72fa4f93-2543-4600-ba03-7de81decd30a';
      this.legacyAppUrl = "https://local.whetstonedata.com:3001";
    } else if (host == "dev-beta.pliable.co" || host == "next-dev-beta.pliable.co") {
      this.authUrl = `https://auth.dev-beta.pliable.co`;
      this.authAppUrl = "https://dev-beta.pliable.co";
      this.feClientId = '512d51ea-262c-496b-8aeb-7644d1dc12dd';
      this.feAppId = '07a2c076-3948-4aa0-ae6f-851bfec6ddff';
      this.legacyAppUrl = "https://legacy-dev-beta.pliable.co";
    } else if (host.startsWith('app.') || host.startsWith('next.')) {
      this.authUrl = "https://auth.app.pliable.co";
      this.authAppUrl = `https://${host.replace('next.', 'app.')}`;
      this.feClientId = 'b1c62231-3a21-48ed-857e-cde8ce0c2303';
      this.feAppId = 'da24bc6c-0351-4e5f-b262-91d924f6de71';
      this.legacyAppUrl = `https://${host.replace('app.', 'legacy.').replace('next.', 'legacy.')}`;
    } else {
      // Custom domain — fetch auth config + SSO info from backend in one call
      // Uses sync XHR because FronteggProvider needs these values at init time
      this.isCustomDomain = true;
      const xhr = new XMLHttpRequest();
      xhr.open('GET', '/api/v1/auth/init', false);
      xhr.send();
      if (xhr.status === 200) {
        const config = JSON.parse(xhr.responseText);
        this.authUrl = config.auth_url;
        this.authAppUrl = config.app_url;
        this.feClientId = config.client_id;
        this.feAppId = config.app_id;
        this.ssoLoginUrl = config.sso_login_url || null;
        this.returnToken = config.return_token || null;
      } else {
        // Fallback — will likely fail but at least doesn't crash
        this.authUrl = `https://auth.${window.location.host}`;
        this.authAppUrl = `https://${window.location.host}`;
      }
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public setAuthState(authState: PlbAuthState) {
    this.plbAuthState = authState;
    this.updateAnalytics(authState);
    this.updateThena(authState);
  }

  private updateThena(authState: PlbAuthState) {
    try {
      window.thena?.setEmail({
        email: authState.email,
        hashedEmail: authState.customClaims?.thena_hmac,
        name: authState.name,
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  private updateAnalytics(authState: PlbAuthState) {
    try {
      // Log rocket needs an identify call on each page load or the session shows up anonymous
      window.analytics?.identify(authState.sub, {
        name: authState.name,
        email: authState.email,
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  public getAuthState(): PlbAuthState | null {
    // First try to get from instance state
    if (this.plbAuthState) {
      return this.plbAuthState;
    }
    
    // Then try sessionStorage
    const sessionState = sessionStorage.getItem('plb_auth_state');
    if (sessionState) {
      try {
        this.plbAuthState = JSON.parse(sessionState);
        return this.plbAuthState;
      } catch (e) {
        console.error('Failed to parse auth state from sessionStorage:', e);
      }
    }
    
    return null;
  }

  public getAccessToken(): string | null {
    // First try to get from instance state
    if (this.plbAuthState?.access_token) {
      return this.plbAuthState.access_token;
    }
    
    // Then try sessionStorage
    const authState = this.getAuthState();
    if (authState?.access_token) {
      return authState.access_token;
    }
    
    // Finally, try to get from Frontegg's store directly
    // Frontegg stores auth state in localStorage/sessionStorage
    try {
      // Try to get from Frontegg's auth state
      const fronteggState = (window as any).__FRONTEGG__?.authState;
      if (fronteggState?.user?.accessToken) {
        return fronteggState.user.accessToken;
      }
      
      // Try localStorage (Frontegg might store it there)
      const fronteggAuth = localStorage.getItem('frontegg_auth');
      if (fronteggAuth) {
        const parsed = JSON.parse(fronteggAuth);
        if (parsed?.user?.accessToken) {
          return parsed.user.accessToken;
        }
      }
    } catch (e) {
      console.debug('Could not get token from Frontegg store:', e);
    }
    
    return null;
  }
}

